import type { Building, BuildingStore } from '../buildings/BuildingStore';
import { statsAt, ECONOMY, STOCK_RESOURCES, buildingDef, type ProductionRule, type ResourceAmounts, type StockResource } from '../buildings/definitions';
import { isPlayerId, type PlayerId } from '../ids';
import { ResourceLedger, isEmptyAmounts, type Stock } from './ResourceLedger';

export type EconomyEvent =
  /** A player's ledger (amounts or capacity) changed. Emitted at most once per player per step / command. */
  | { type: 'changed'; playerId: PlayerId }
  | { type: 'produced'; playerId: PlayerId; buildingId: number; resource: StockResource; amount: number }
  /** A producer stopped because storage is full (stalled = true) or started again (false). */
  | { type: 'stalled'; playerId: PlayerId; buildingId: number; stalled: boolean }
  /** Survivors ate. `eaten < needed` means there was not enough food. */
  | { type: 'upkeep'; playerId: PlayerId; needed: number; eaten: number }
  /** Food shortage started (true) or ended (false). */
  | { type: 'shortage'; playerId: PlayerId; shortage: boolean }
  /** Resources destroyed because storage shrank (e.g. a Warehouse was demolished). */
  | { type: 'lost'; playerId: PlayerId; amounts: ResourceAmounts };

export interface ProductionStatus {
  readonly rule: ProductionRule;
  /** Seconds until the next output at the current staffing (0 while stalled, Infinity with no workers). */
  readonly remaining: number;
  /** Storage full: waiting for space. */
  readonly stalled: boolean;
  /** Share of full speed (workers / slots). 0 = nobody working. */
  readonly speed: number;
}

/**
 * What the economy needs from the colony (Phase 4): how hard each workplace
 * is staffed, how much food the survivors eat, and what happens on a shortfall.
 */
export interface Workforce {
  workRatio(buildingId: number): number;
  foodNeeded(p: PlayerId): number;
  onShortfall(p: PlayerId): void;
}

export interface EconomySnapshot {
  readonly players: Record<string, { amounts: Stock; capacity: Stock; shortage: boolean }>;
  /** Elapsed production time per producing building (id → seconds), for determinism checks and saves. */
  readonly production: Record<string, number>;
  readonly upkeepElapsed: number;
}

const EPS = 1e-9;

/**
 * Per-player economy: ledgers, storage capacity, production and upkeep.
 * Engine-free; advanced only by `step(dt)` from the simulation's fixed ticks,
 * so it is frame-rate independent and deterministic.
 *
 * Rules (DECISIONS D45):
 *  - Storage capacity = sum of `storage` of the player's COMPLETE buildings.
 *    If capacity shrinks, the excess is lost (reported as a `lost` event).
 *  - A complete producer adds `amount` every `intervalSeconds` at FULL staffing;
 *    its timer runs at workers / slots speed (Phase 4), so no workers = no output.
 *    If storage is full it stalls (timer holds) and resumes as soon as there is
 *    space; when only part fits, the part that fits is stored.
 *  - Every `upkeepIntervalSeconds` each player's survivors eat (Workforce.foodNeeded).
 *    Food never goes below zero; not enough food = shortage, and the colony is
 *    told (Workforce.onShortfall).
 */
export class Economy {
  private readonly ledgers = new Map<PlayerId, ResourceLedger>();
  /** Elapsed seconds since the last output, per COMPLETE producing building. */
  private readonly timers = new Map<number, number>();
  private readonly stalled = new Set<number>();
  /** Producers that completed during the current tick: they start counting from the NEXT tick. */
  private readonly fresh = new Set<number>();
  private readonly shortage = new Map<PlayerId, boolean>();
  private readonly listeners = new Set<(e: EconomyEvent) => void>();
  private readonly dirty = new Set<PlayerId>();
  /** D128: research bonuses (set by the Simulation). */
  bonus: (owner: PlayerId, key: 'prod_food' | 'prod_wood' | 'prod_scrap') => number = () => 1;
  private upkeepElapsed = 0;
  private readonly unsubscribe: () => void;

  constructor(
    private readonly store: BuildingStore,
    players: readonly PlayerId[],
    start: ResourceAmounts,
    private readonly workforce: Workforce,
  ) {
    for (const p of players) {
      this.ledgers.set(p, new ResourceLedger(start, this.capacityFor(p)));
      this.shortage.set(p, false);
    }
    for (const b of store.all()) if (b.state === 'complete' && buildingDef(b.type).production) this.timers.set(b.id, 0);
    this.unsubscribe = store.subscribe((e) => {
      if (e.type === 'completed' || (e.type === 'added' && e.building.state === 'complete')) this.onComplete(e.building);
      else if (e.type === 'removed') this.onRemoved(e.building);
      else if (e.type === 'upgraded' && isPlayerId(e.building.ownerId) && this.ledgers.has(e.building.ownerId) && statsAt(e.building.type, e.building.level).storage) this.refreshCapacity(e.building.ownerId);
    });
  }

  get listenerCount(): number {
    return this.listeners.size;
  }

  subscribe(l: (e: EconomyEvent) => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  dispose(): void {
    this.unsubscribe();
    this.listeners.clear();
  }

  ledger(p: PlayerId): ResourceLedger {
    const l = this.ledgers.get(p);
    if (!l) throw new Error(`No ledger for ${p}`);
    return l;
  }

  hasShortage(p: PlayerId): boolean {
    return this.shortage.get(p) ?? false;
  }

  /** Storage capacity from the player's complete buildings. */
  capacityFor(p: PlayerId): Stock {
    const cap: Stock = { wood: 0, scrap: 0, food: 0 };
    for (const b of this.store.ownedBy(p)) {
      const s = statsAt(b.type, b.level).storage;
      if (!s || b.state !== 'complete') continue;
      for (const r of STOCK_RESOURCES) cap[r] += s[r] ?? 0;
    }
    return cap;
  }

  /** Food the player's survivors eat per upkeep cycle. */
  upkeepFor(p: PlayerId): number {
    return this.workforce.foodNeeded(p);
  }

  /** Seconds until the next upkeep cycle. */
  get upkeepRemaining(): number {
    return Math.max(0, ECONOMY.upkeepIntervalSeconds - this.upkeepElapsed);
  }

  /** Production status of a building, or null if it does not (yet) produce. */
  productionStatus(buildingId: number): ProductionStatus | null {
    const elapsed = this.timers.get(buildingId);
    const b = this.store.get(buildingId);
    const rule = b ? buildingDef(b.type).production : undefined;
    if (elapsed === undefined || !rule) return null;
    const stalled = this.stalled.has(buildingId);
    const speed = this.workforce.workRatio(buildingId);
    const remaining = stalled ? 0 : speed > 0 ? Math.max(0, rule.intervalSeconds - elapsed) / speed : Infinity;
    return { rule, remaining, stalled, speed };
  }

  /** Record that a player's ledger changed outside `step` (spend / refund); flushed by `flush`. */
  markChanged(p: PlayerId): void {
    this.dirty.add(p);
  }

  /** Emit pending `changed` events (one per player). */
  flush(): void {
    for (const p of [...this.dirty].sort()) this.emit({ type: 'changed', playerId: p });
    this.dirty.clear();
  }

  /** Advance production and upkeep by dt seconds of simulation time. */
  step(dt: number): void {
    if (!(dt > 0)) return;
    for (const id of [...this.timers.keys()].sort((a, b) => a - b)) if (!this.fresh.has(id)) this.stepProducer(id, dt);
    this.fresh.clear();
    this.upkeepElapsed += dt;
    while (this.upkeepElapsed >= ECONOMY.upkeepIntervalSeconds - EPS) {
      this.upkeepElapsed = Math.max(0, this.upkeepElapsed - ECONOMY.upkeepIntervalSeconds);
      for (const p of [...this.ledgers.keys()].sort()) this.eat(p);
    }
    this.flush();
  }

  snapshot(): EconomySnapshot {
    const players: EconomySnapshot['players'] = {};
    for (const [p, l] of [...this.ledgers.entries()].sort(([a], [b]) => a.localeCompare(b))) players[p] = { ...l.toJSON(), shortage: this.hasShortage(p) };
    const production: Record<string, number> = {};
    for (const id of [...this.timers.keys()].sort((a, b) => a - b)) production[String(id)] = this.timers.get(id)!;
    return { players, production, upkeepElapsed: this.upkeepElapsed };
  }

  // ------------------------------------------------------------------

  private stepProducer(id: number, dt: number): void {
    const b = this.store.get(id);
    if (!b || !isPlayerId(b.ownerId)) return;
    const owner = b.ownerId;
    const rule = buildingDef(b.type).production!;
    const ledger = this.ledger(owner);
    const speed = this.workforce.workRatio(id);
    if (speed <= 0 && !this.stalled.has(id)) return; // nobody working: the timer holds
    let elapsed = this.timers.get(id)! + dt * speed * this.bonus(owner, `prod_${rule.resource}`);
    while (elapsed >= rule.intervalSeconds - EPS) {
      if (ledger.space(rule.resource) === 0) {
        // Storage full: hold the finished batch until there is room.
        elapsed = rule.intervalSeconds;
        this.setStalled(owner, id, true);
        break;
      }
      const { stored } = ledger.add(rule.resource, rule.amount);
      elapsed = Math.max(0, elapsed - rule.intervalSeconds);
      this.setStalled(owner, id, false);
      this.dirty.add(owner);
      this.emit({ type: 'produced', playerId: owner, buildingId: id, resource: rule.resource, amount: stored });
    }
    this.timers.set(id, elapsed);
  }

  private eat(p: PlayerId): void {
    const needed = this.upkeepFor(p);
    if (needed === 0) return;
    const eaten = this.ledger(p).take('food', needed);
    if (eaten > 0) this.dirty.add(p);
    this.emit({ type: 'upkeep', playerId: p, needed, eaten });
    const short = eaten < needed;
    if (short) this.workforce.onShortfall(p);
    if (short !== this.hasShortage(p)) {
      this.shortage.set(p, short);
      this.emit({ type: 'shortage', playerId: p, shortage: short });
    }
  }

  private setStalled(p: PlayerId, id: number, stalled: boolean): void {
    if (this.stalled.has(id) === stalled) return;
    if (stalled) this.stalled.add(id);
    else this.stalled.delete(id);
    this.emit({ type: 'stalled', playerId: p, buildingId: id, stalled });
  }

  private onComplete(b: Building): void {
    if (!isPlayerId(b.ownerId) || !this.ledgers.has(b.ownerId)) return;
    const def = buildingDef(b.type);
    if (def.production && !this.timers.has(b.id)) {
      this.timers.set(b.id, 0);
      this.fresh.add(b.id);
    }
    if (def.storage) this.refreshCapacity(b.ownerId);
  }

  private onRemoved(b: Building): void {
    this.timers.delete(b.id);
    this.stalled.delete(b.id);
    this.fresh.delete(b.id);
    if (!isPlayerId(b.ownerId) || !this.ledgers.has(b.ownerId)) return;
    if (buildingDef(b.type).storage && b.state === 'complete') this.refreshCapacity(b.ownerId);
  }

  private refreshCapacity(p: PlayerId): void {
    const lost = this.ledger(p).setCapacity(this.capacityFor(p));
    this.dirty.add(p);
    if (!isEmptyAmounts(lost)) this.emit({ type: 'lost', playerId: p, amounts: lost });
  }

  private emit(e: EconomyEvent): void {
    for (const l of this.listeners) l(e);
  }
}
